import React, { useCallback, useEffect, useRef, useState } from "react";
import { ScanLine, FileText, X, AlertTriangle, CheckCircle2 } from "lucide-react";

export const MAX_FILES = 20;
export const MAX_BYTES = 50 * 1024 * 1024;
const isPdf = (f) => /pdf$/i.test(f.type) || /\.pdf$/i.test(f.name);

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Assess a queued list → per-file reason + validity, honoring the 20-file cap.
function assess(items) {
  let validCount = 0;
  return items.map(({ id, file }) => {
    let reason = null;
    if (!isPdf(file)) reason = "PDF only";
    else if (file.size <= 0) reason = "Empty file";
    else if (file.size > MAX_BYTES) reason = "Over 50 MB";
    else if (validCount >= MAX_FILES) reason = "Batch limit (20)";
    const ok = !reason;
    if (ok) validCount += 1;
    return { id, file, ok, reason };
  });
}

// Modern drag-and-drop selector for the "Add New Check" flow. Upload & forget:
// on confirm we hand back only the valid PDFs and close.
export function UploadDropzone({ open, onClose, onConfirm }) {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(false);

  const reset = useCallback(() => { setItems([]); setDrag(false); }, []);
  const close = useCallback(() => { reset(); onClose?.(); }, [reset, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => { if (!open) reset(); }, [open, reset]);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []).map((file) => ({
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, file,
    }));
    if (!incoming.length) return;
    setItems((prev) => {
      const seen = new Set(prev.map((p) => `${p.file.name}_${p.file.size}`));
      const deduped = incoming.filter((i) => !seen.has(`${i.file.name}_${i.file.size}`));
      return [...prev, ...deduped];
    });
  }, []);

  if (!open) return null;

  const assessed = assess(items);
  const valid = assessed.filter((a) => a.ok);
  const invalid = assessed.filter((a) => !a.ok);

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    addFiles(e.dataTransfer?.files);
  };
  const onBrowse = (e) => { addFiles(e.target.files); e.target.value = ""; };
  const removeItem = (id) => setItems((prev) => prev.filter((p) => p.id !== id));

  const confirm = () => {
    if (!valid.length) return;
    onConfirm?.(valid.map((v) => v.file));
    close();
  };

  return (
    <>
      <div className="pp-dz-backdrop" onClick={close} data-testid="pp-dz-backdrop" />
      <div className="pp-dz" role="dialog" aria-label="Add new checks" data-testid="pp-dz">
        <div className="pp-dz-head">
          <div>
            <div className="pp-dz-title">Add new checks</div>
            <div className="pp-dz-sub">Upload up to {MAX_FILES} files · Multiple PDF only · up to 50 MB each</div>
          </div>
          <button className="pp-dz-close" onClick={close} data-testid="pp-dz-close" aria-label="Close"><X size={16} /></button>
        </div>

        <div className="pp-dz-body">
          <div
            className={`pp-dz-zone ${drag ? "pp-drag" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
            onDrop={onDrop}
            data-testid="pp-dz-zone"
          >
            <input ref={inputRef} type="file" multiple accept=".pdf,application/pdf"
              style={{ display: "none" }} onChange={onBrowse} data-testid="pp-dz-input" />
            <div className="pp-dz-ic"><ScanLine size={26} strokeWidth={1.8} /></div>
            <div className="pp-dz-cta">Drag &amp; drop PDFs here, or <span>click to browse</span></div>
            <div className="pp-dz-hint">Come and upload up to {MAX_FILES} files in one go — Lens reads them in the background.</div>
          </div>

          {items.length > 0 ? (
            <div className="pp-dz-list" data-testid="pp-dz-list">
              {assessed.map((a) => (
                <div key={a.id} className={`pp-dz-item ${a.ok ? "" : "pp-bad"}`} data-testid="pp-dz-item">
                  <div className={`pp-dz-fic ${a.ok ? "" : "pp-bad"}`}>
                    {a.ok ? <FileText size={15} /> : <AlertTriangle size={15} />}
                  </div>
                  <div className="pp-dz-fb">
                    <div className="pp-dz-fn">{a.file.name}</div>
                    <div className={`pp-dz-fm ${a.ok ? "" : "pp-bad"}`}>
                      {a.ok ? humanSize(a.file.size) : `Skipped — ${a.reason}`}
                    </div>
                  </div>
                  <button className="pp-dz-rm" onClick={() => removeItem(a.id)} aria-label="Remove"
                    data-testid="pp-dz-remove"><X size={13} /></button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="pp-dz-foot">
          <div className="pp-dz-count" data-testid="pp-dz-count">
            {valid.length > 0 ? (
              <><CheckCircle2 size={14} /> {valid.length} ready{invalid.length ? ` · ${invalid.length} skipped` : ""}</>
            ) : items.length ? (
              <><AlertTriangle size={14} /> No valid PDFs yet</>
            ) : (
              <>Nothing queued</>
            )}
          </div>
          <div className="pp-dz-actions">
            <button className="pp-btn pp-ghost" onClick={close} data-testid="pp-dz-cancel">Cancel</button>
            <button className="pp-btn pp-dark" onClick={confirm} disabled={!valid.length} data-testid="pp-dz-confirm">
              <ScanLine size={14} /> Upload {valid.length || ""} {valid.length === 1 ? "file" : "files"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
